/**
 * Baby Development Medical Page
 * Manage U-Untersuchung info, normal behaviors, and red flags
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Plus, Trash2, Stethoscope, CheckCircle, AlertTriangle, Baby } from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';
import type { BabyDevelopmentPhase, BabyDevelopmentMedical } from './types';

export function BabyDevelopmentMedicalPage() {
  const supabase = getSupabaseAdminClient();
  const { id: phaseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();

  const [formData, setFormData] = useState({
    u_exam_name: '',
    u_exam_timeframe: '',
    u_exam_checks: [''],
    normal_behaviors: [''],
    red_flags: [''],
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch phase
  const { data: phase } = useQuery({
    queryKey: ['baby-development-phase', phaseId, contentLocale],
    queryFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const { data, error } = await (supabase as any)
        .from('baby_development_phases')
        .select('*')
        .eq('id', phaseId)
        .eq('locale', contentLocale)
        .single();
      if (error) throw error;
      return data as BabyDevelopmentPhase;
    },
    enabled: !!phaseId,
  });

  // Fetch medical info
  const { data: medical, isLoading } = useQuery({
    queryKey: ['baby-development-medical', phaseId, contentLocale],
    queryFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const { data, error } = await (supabase as any)
        .from('baby_development_medical')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('locale', contentLocale)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data as BabyDevelopmentMedical | null;
    },
    enabled: !!phaseId,
  });

  // Initialize form from data
  useEffect(() => {
    if (medical) {
      setFormData({
        u_exam_name: medical.u_exam_name || '',
        u_exam_timeframe: medical.u_exam_timeframe || '',
        u_exam_checks: medical.u_exam_checks.length > 0 ? medical.u_exam_checks : [''],
        normal_behaviors: medical.normal_behaviors.length > 0 ? medical.normal_behaviors : [''],
        red_flags: medical.red_flags.length > 0 ? medical.red_flags : [''],
      });
      setHasChanges(false);
    }
  }, [medical]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const dataToSave = {
        phase_id: phaseId,
        u_exam_name: formData.u_exam_name || null,
        u_exam_timeframe: formData.u_exam_timeframe || null,
        u_exam_checks: formData.u_exam_checks.filter(c => c.trim()),
        normal_behaviors: formData.normal_behaviors.filter(b => b.trim()),
        red_flags: formData.red_flags.filter(f => f.trim()),
        locale: contentLocale,
      };

      if (medical) {
        const { error } = await (supabase as any)
          .from('baby_development_medical')
          .update(dataToSave)
          .eq('id', medical.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any)
          .from('baby_development_medical')
          .insert(dataToSave);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success('Medizin-Infos gespeichert');
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['baby-development-medical', phaseId, contentLocale] });
    },
    onError: (error) => {
      toast.error('Fehler: ' + (error as Error).message);
    },
  });

  // List item management helpers
  const updateListItem = (
    field: 'u_exam_checks' | 'normal_behaviors' | 'red_flags',
    index: number,
    value: string
  ) => {
    const newList = [...formData[field]];
    newList[index] = value;
    setFormData({ ...formData, [field]: newList });
    setHasChanges(true);
  };

  const addListItem = (field: 'u_exam_checks' | 'normal_behaviors' | 'red_flags') => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
    setHasChanges(true);
  };

  const removeListItem = (
    field: 'u_exam_checks' | 'normal_behaviors' | 'red_flags',
    index: number
  ) => {
    const newList = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newList.length > 0 ? newList : [''] });
    setHasChanges(true);
  };

  // Early return after all hooks
  if (!phaseId) {
    return <div className="text-center py-12 text-slate-500">Phase nicht gefunden</div>;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/features/baby-development')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
            <Baby className="w-6 h-6 text-teal-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Medizin-Infos: {phase?.display_range ?? ''}</h1>
            <p className="text-slate-500">Phase {phase?.phase_number}: "{phase?.subtitle}"</p>
          </div>
          <LocaleSelector />
        </div>
      </div>

      <div className="space-y-6">
        {/* U-Untersuchung */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Stethoscope className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-slate-900">U-Untersuchung</h2>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name (z.B. "U5")
              </label>
              <input
                type="text"
                value={formData.u_exam_name}
                onChange={(e) => {
                  setFormData({ ...formData, u_exam_name: e.target.value });
                  setHasChanges(true);
                }}
                placeholder="U5"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Zeitraum
              </label>
              <input
                type="text"
                value={formData.u_exam_timeframe}
                onChange={(e) => {
                  setFormData({ ...formData, u_exam_timeframe: e.target.value });
                  setHasChanges(true);
                }}
                placeholder="6.-7. Lebensmonat"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Was wird geprüft?
            </label>
            {formData.u_exam_checks.map((check, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={check}
                  onChange={(e) => updateListItem('u_exam_checks', index, e.target.value)}
                  placeholder="z.B. Körperliche Entwicklung"
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
                <button
                  onClick={() => removeListItem('u_exam_checks', index)}
                  className="p-2 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            ))}
            <button
              onClick={() => addListItem('u_exam_checks')}
              className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 mt-2"
            >
              <Plus className="w-4 h-4" />
              Punkt hinzufügen
            </button>
          </div>
        </div>

        {/* Normal Behaviors */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-slate-900">Das ist normal</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Verhaltensweisen, die in diesem Alter völlig normal sind und Eltern beruhigen sollten.
          </p>

          {formData.normal_behaviors.map((behavior, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <input
                type="text"
                value={behavior}
                onChange={(e) => updateListItem('normal_behaviors', index, e.target.value)}
                placeholder="z.B. Vermehrtes Sabbern"
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={() => removeListItem('normal_behaviors', index)}
                className="p-2 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          ))}
          <button
            onClick={() => addListItem('normal_behaviors')}
            className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 mt-2"
          >
            <Plus className="w-4 h-4" />
            Punkt hinzufügen
          </button>
        </div>

        {/* Red Flags */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h2 className="text-lg font-semibold text-slate-900">Ärztlich abklären</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Warnzeichen, bei denen Eltern einen Kinderarzt konsultieren sollten.
          </p>

          {formData.red_flags.map((flag, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <input
                type="text"
                value={flag}
                onChange={(e) => updateListItem('red_flags', index, e.target.value)}
                placeholder="z.B. Kein Blickkontakt"
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={() => removeListItem('red_flags', index)}
                className="p-2 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          ))}
          <button
            onClick={() => addListItem('red_flags')}
            className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 mt-2"
          >
            <Plus className="w-4 h-4" />
            Punkt hinzufügen
          </button>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || saveMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
